import React, { useEffect, useRef, useState } from "react";

/**
 * Magnetic equalizer ruler — a horizontal row of vertical bars whose heights
 * rise into a bell-curve centered on the cursor X position (within the parent),
 * like a compass-reading. Idle: thin uniform line.
 *
 * Props:
 *  - barCount: how many bars across the width
 *  - peakHeight: max bar height in px
 *  - baseHeight: idle bar height in px
 *  - falloff: bell curve width (smaller = sharper peak)
 *  - color: bar color (CSS)
 *  - className: container class
 */
export const MagneticBar = ({
    barCount = 64,
    peakHeight = 16,
    baseHeight = 3,
    falloff = 0.08,
    color = "currentColor",
    className = "",
    style = {},
    active = false,
}) => {
    const ref = useRef(null);
    const [cursorRel, setCursorRel] = useState(null); // 0..1 across width; null = idle

    useEffect(() => {
        const node = ref.current;
        if (!node) return;
        const parent = node.parentElement;
        if (!parent) return;
        const onMove = (e) => {
            const rect = parent.getBoundingClientRect();
            const x = (e.clientX - rect.left) / rect.width;
            if (x < -0.15 || x > 1.15) {
                setCursorRel(null);
            } else {
                setCursorRel(Math.max(0, Math.min(1, x)));
            }
        };
        const onLeave = () => setCursorRel(null);
        parent.addEventListener("mousemove", onMove);
        parent.addEventListener("mouseleave", onLeave);
        return () => {
            parent.removeEventListener("mousemove", onMove);
            parent.removeEventListener("mouseleave", onLeave);
        };
    }, []);

    const visible = active || cursorRel !== null;

    const bars = [];
    for (let i = 0; i < barCount; i++) {
        const t = i / (barCount - 1);
        let h = baseHeight;
        if (cursorRel !== null) {
            const d = Math.abs(t - cursorRel);
            // Bell curve
            const g = Math.exp(-(d * d) / (2 * falloff * falloff));
            h = baseHeight + (peakHeight - baseHeight) * g;
        } else if (active) {
            // gentle pulse when no cursor but active
            const phase = Math.abs(t - 0.5);
            h = baseHeight + (peakHeight - baseHeight) * Math.exp(-phase * phase * 30) * 0.4;
        }
        bars.push(h);
    }

    return (
        <div
            ref={ref}
            className={className}
            style={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                width: "100%",
                height: peakHeight,
                pointerEvents: "none",
                opacity: visible ? 1 : 0.55,
                transition: "opacity 0.4s cubic-bezier(0.65, 0, 0.35, 1)",
                ...style,
            }}
            aria-hidden="true"
        >
            {bars.map((h, i) => (
                <span
                    key={i}
                    style={{
                        width: 1,
                        height: h,
                        background: color,
                        transition: "height 0.18s cubic-bezier(0.22, 1, 0.36, 1)",
                        display: "inline-block",
                        flexShrink: 0,
                    }}
                />
            ))}
        </div>
    );
};

export default MagneticBar;
